/**
 * Size tokens of the widget grid (design handoff §"Grid system").
 *
 * 12 columns, 20 px gutter, 60 px row unit. A widget occupies `cols` x `rows` grid units;
 * the S tile (3 x 2 = 140 px) is exactly today's KPI tile, M (6 x 4 = 300 px) is a chart.
 */
export const GRID = { cols: 12, gap: 20, rowHeight: 60 };

export const SIZES = {
  R: { cols: 12, rows: 1, labelKey: 'dashboard.sizes.R' },
  S: { cols: 3, rows: 2, labelKey: 'dashboard.sizes.S' },
  W: { cols: 4, rows: 2, labelKey: 'dashboard.sizes.W' },
  MS: { cols: 6, rows: 2, labelKey: 'dashboard.sizes.MS' },
  M: { cols: 6, rows: 4, labelKey: 'dashboard.sizes.M' },
  MT: { cols: 6, rows: 6, labelKey: 'dashboard.sizes.MT' },
  L: { cols: 12, rows: 4, labelKey: 'dashboard.sizes.L' },
  LT: { cols: 12, rows: 6, labelKey: 'dashboard.sizes.LT' },
  // 540 px: a full-width list of ten 48 px rows (nine plus the footer). Added after the
  // design: the row height grew from 40 to 48 px and Klassinen must not show less than before.
  LX: { cols: 12, rows: 8, labelKey: 'dashboard.sizes.LX' },
};

export const SIZE_TOKENS = Object.keys(SIZES);

// The S / M / L toggle groups. A second click on the active group cycles inside it.
export const SIZE_GROUPS = {
  S: ['S', 'W'],
  M: ['MS', 'M', 'MT'],
  L: ['L', 'LT', 'LX', 'R'],
};
export const GROUP_ORDER = ['S', 'M', 'L'];

export function groupOf(size) {
  return GROUP_ORDER.find((group) => SIZE_GROUPS[group].includes(size)) || 'M';
}

export function availableGroups(entry) {
  return GROUP_ORDER.filter((group) => SIZE_GROUPS[group].some((token) => entry.sizes.includes(token)));
}

/** Next size when the user clicks `group`: first supported token of the group, or the next one. */
export function nextSizeForGroup(entry, currentSize, group) {
  const tokens = SIZE_GROUPS[group].filter((token) => entry.sizes.includes(token));
  if (!tokens.length) return currentSize;
  const index = tokens.indexOf(currentSize);
  if (index === -1) return tokens[0];
  return tokens[(index + 1) % tokens.length];
}

/** Pixel height of a size on desktop (rows * unit + inner gutters). */
export function sizeHeightPx(size) {
  const { rows } = SIZES[size] || SIZES.M;
  return rows * GRID.rowHeight + (rows - 1) * GRID.gap;
}

/** Approximate desktop pixel width of a size at the 1440 px reference (1116 px content). */
export function sizeWidthPx(size) {
  const { cols } = SIZES[size] || SIZES.M;
  const column = (1116 - (GRID.cols - 1) * GRID.gap) / GRID.cols;
  return Math.round(cols * column + (cols - 1) * GRID.gap);
}

/** Body height available under a 48 px header at a given size (for list row capacity). */
export function bodyHeightPx(size, hasHeader = true) {
  return sizeHeightPx(size) - (hasHeader ? 48 : 0);
}

/** Templates that never get a header (design: S/W tiles, button row, banner, clock). */
export function isHeadless(template, size) {
  if (['kpi', 'clock', 'buttons', 'banner'].includes(template)) return true;
  if (['breakdown', 'list', 'action', 'feedback'].includes(template) && (size === 'S' || size === 'W')) return true;
  if (template === 'search' && size === 'R') return true;
  return false;
}

/** Mobile (< md): two columns, auto rows. Tiles take one column, everything else both. */
export function mobileSlotStyle(entry, size) {
  const tile = isHeadless(entry.template, size) && entry.template !== 'clock' && entry.template !== 'buttons' && entry.template !== 'banner';
  const minHeight = tile
    ? 132
    : entry.template === 'buttons' || entry.template === 'banner'
      ? 'auto'
      : entry.template === 'timeline'
        ? 260
        : entry.template === 'search'
          ? 120
          : size === 'LX'
            ? 480
            : size === 'MT' || size === 'LT'
              ? 420
              : 300;
  return { gridColumn: tile ? 'span 1' : 'span 2', minHeight };
}
